import { Link } from "react-router-dom";
import { Shield, Ban, Users, Camera, FileText, AlertTriangle, ArrowLeft, Lock } from "lucide-react";

const C = {
  purple: "#5B2A86",
  purpleDeep: "#3F1A66",
  gold: "#D4AF37",
  cream: "#FAF6EC",
  ink: "#1A1A1A",
};

const Section = ({ id, icon: Icon, title, color, children }) => (
  <section id={id} className="scroll-mt-24 rounded-2xl p-6 sm:p-8 mb-5 bg-white shadow-sm border" style={{ borderColor: `${color}30` }} data-testid={`policy-${id}`}>
    <div className="flex items-center gap-3 mb-4 pb-3 border-b" style={{ borderColor: `${color}20` }}>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}15`, color }}>
        <Icon className="w-5 h-5" strokeWidth={2.4} />
      </div>
      <h2 className="text-xl sm:text-2xl font-extrabold" style={{ color: C.purpleDeep }}>{title}</h2>
    </div>
    <div className="text-[14.5px] leading-relaxed text-gray-800 space-y-3">{children}</div>
  </section>
);

const MarketCampPoliciesPage = () => {
  return (
    <div className="min-h-screen" style={{ background: C.cream }} data-testid="market-camp-policies-page">
      {/* Top bar */}
      <div className="sticky top-0 z-30 backdrop-blur" style={{ background: "rgba(250,246,236,0.92)", borderBottom: `1px solid ${C.purple}20` }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/mini-makers-market-camp" className="inline-flex items-center gap-2 text-sm font-bold" style={{ color: C.purpleDeep }} data-testid="back-to-camp">
            <ArrowLeft className="w-4 h-4" /> Back to registration
          </Link>
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-widest" style={{ color: C.gold }}>Camp Policies &amp; Waiver</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <p className="text-[11px] tracking-[4px] font-extrabold uppercase mb-2" style={{ color: C.purple }}>Mix &amp; Mingle Events</p>
          <h1 className="text-3xl sm:text-4xl font-black mb-2" style={{ color: C.purpleDeep }}>Mini Makers Market Camp — Policies &amp; Waiver</h1>
          <p className="text-sm text-gray-600 max-w-2xl mx-auto">
            Please read these policies carefully before completing your registration. By accepting consent #8 at checkout,
            you confirm that you understand and agree to everything below.
          </p>
        </div>

        {/* TOC */}
        <nav className="rounded-2xl p-4 mb-6 border bg-white" style={{ borderColor: `${C.purple}25` }}>
          <p className="text-[10px] font-extrabold uppercase tracking-widest mb-2" style={{ color: C.gold }}>Quick jump</p>
          <ul className="grid sm:grid-cols-2 gap-1.5 text-sm">
            {[
              ["camp-policies", "1. Camp Policies"],
              ["no-refund", "2. NO-REFUND Policy"],
              ["conduct", "3. Conduct Policy"],
              ["pickup", "4. Pickup &amp; Safety Policy"],
              ["media", "5. Media Consent"],
              ["privacy", "6. Privacy Terms"],
              ["waiver", "7. Participation Waiver"],
            ].map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`} className="block px-3 py-1.5 rounded-md hover:bg-purple-50 font-semibold" style={{ color: C.purpleDeep }} dangerouslySetInnerHTML={{ __html: `→ ${label}` }} />
              </li>
            ))}
          </ul>
        </nav>

        <Section id="camp-policies" icon={FileText} title="1. Camp Policies — Overview" color={C.purple}>
          <p>
            Mini Makers Market Camp is a structured, multi-day creative camp for children aged 4–15 hosted by
            Mix &amp; Mingle Events in Edmonton, Alberta. Registration is per child for the full camp duration.
            Materials, snacks, supplies, and instruction are pre-purchased on a per-registrant basis.
          </p>
          <p>
            <strong>Drop-off &amp; pick-up windows are firm.</strong> Late drop-off may result in your child missing portions of
            time-blocked activities (we cannot rewind a workshop). Late pickup may incur a supervision surcharge published on
            the camp page.
          </p>
          <p>
            <strong>Allergies, dietary needs, and medical conditions</strong> must be disclosed at registration. We will do everything
            reasonable to accommodate, but cannot guarantee a perfectly allergen-free environment.
          </p>
        </Section>

        <Section id="no-refund" icon={Ban} title="2. NO-REFUND POLICY" color="#B91C1C">
          <div className="rounded-xl p-4 mb-2 border-l-4" style={{ background: "#FEF2F2", borderColor: "#B91C1C" }}>
            <p className="font-bold" style={{ color: "#7F1D1D" }}>
              Registration fees are <span className="underline">non-refundable</span>, including in the event of no-show, illness,
              schedule conflicts, or a change of plans.
            </p>
          </div>
          <p>
            Because we pre-purchase materials, snacks, instructor blocks, and venue space on the day you register,
            cancellations cannot be refunded once a spot is reserved.
          </p>
          <p>
            <strong>Transfers are allowed.</strong> You may transfer your child&apos;s spot to another family of your choosing,
            free of charge, up to 48 hours before camp begins — simply email the new family&apos;s contact details, child age,
            and authorized-pickup info to <a href="mailto:hello@mixandmingleevents.com" className="underline font-semibold" style={{ color: C.purple }}>hello@mixandmingleevents.com</a> and we&apos;ll reissue the ticket.
          </p>
          <p>
            If we cancel the camp ourselves (rare — e.g. force majeure or governmental order), you receive a full refund
            or a transferable credit toward a future Mix &amp; Mingle Events camp.
          </p>
        </Section>

        <Section id="conduct" icon={Users} title="3. Conduct Policy" color="#0FA9A0">
          <p>
            We expect a kind, inclusive, and respectful environment from <strong>all</strong> participants — children, parents,
            volunteers, and staff. Behavior that endangers, harasses, bullies, or discriminates against another participant
            will result in immediate dismissal from camp without a refund.
          </p>
          <p>
            Camp staff have full authority to take reasonable actions to maintain safety, including but not limited to:
            separating children, contacting parents to collect early, or contacting emergency services if warranted.
          </p>
        </Section>

        <Section id="pickup" icon={Shield} title="4. Pickup &amp; Safety Policy" color={C.purpleDeep}>
          <div className="rounded-xl p-4 mb-2 border-l-4" style={{ background: "#F5F3FF", borderColor: C.purple }}>
            <p className="font-bold" style={{ color: C.purpleDeep }}>
              For your child&apos;s safety, we require <span className="underline">government-issued photo ID + a matching selfie</span> at registration.
            </p>
          </div>
          <p>
            At pickup, our staff will verify the photo ID and selfie of every authorized pickup person against the
            individual collecting the child. If the photos do not match — or if the person collecting is not on the
            authorized-pickup list — we will contact the listed parents and emergency contacts before releasing the child.
          </p>
          <p>
            Children are released only to adults named on the registration. Add or remove authorized pickup people any time
            by emailing <a href="mailto:hello@mixandmingleevents.com" className="underline font-semibold" style={{ color: C.purple }}>hello@mixandmingleevents.com</a> with the registration code.
          </p>
          <p>
            Photos are stored securely and viewed only by camp staff for the duration of camp + 30 days, then deleted.
          </p>
        </Section>

        <Section id="media" icon={Camera} title="5. Media Consent" color="#D97706">
          <p>
            We may photograph and video-record camp activities for our website, social media, and marketing.
            You consent to your child&apos;s likeness being included in these materials by accepting the media-consent toggle
            at registration. If you <strong>do not</strong> want your child photographed, uncheck the <em>photo consent</em>
            box for that child and notify staff at drop-off — we&apos;ll use a coloured wristband so photographers know to
            exclude them.
          </p>
        </Section>

        <Section id="privacy" icon={Lock} title="6. Privacy Terms" color="#1F4E8A">
          <p>
            We collect only the information needed to run the camp safely and contact you in an emergency: parent &amp;
            child names, contact details, emergency contacts, dietary &amp; medical info, authorized-pickup details, and
            payment confirmation tokens (we do not store credit card numbers — those are handled directly by Stripe).
          </p>
          <p>
            We never sell or share your data. Read our full
            <Link to="/privacy" className="underline font-semibold mx-1" style={{ color: C.purple }}>Privacy Policy</Link>
            for retention windows, your rights, and how to request deletion.
          </p>
        </Section>

        <Section id="waiver" icon={AlertTriangle} title="7. Participation Waiver" color="#B45309">
          <p>
            You acknowledge that camp activities (including but not limited to crafting, baking, market role-play, group
            games, and supervised outdoor play) carry inherent risks. By registering, you agree to release Mix &amp; Mingle
            Events, its staff, contractors, volunteers, and venue partners from liability for ordinary risks associated with
            participation in a children&apos;s camp.
          </p>
          <p>
            You authorize camp staff to seek emergency medical assistance for your child if necessary and confirm that the
            child&apos;s medical, allergy, and dietary information provided is complete and accurate.
          </p>
          <p className="text-xs text-gray-500 mt-4">
            This waiver does not limit liability for gross negligence or willful misconduct, and does not waive rights that
            cannot be waived under applicable Alberta law.
          </p>
        </Section>

        <div className="text-center mt-10">
          <Link
            to="/mini-makers-market-camp"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-extrabold text-white shadow"
            style={{ background: `linear-gradient(135deg, ${C.purple} 0%, ${C.purpleDeep} 100%)` }}
            data-testid="back-to-camp-bottom"
          >
            <ArrowLeft className="w-4 h-4" /> Back to registration
          </Link>
          <p className="text-xs text-gray-500 mt-4">
            Questions? Email <a href="mailto:hello@mixandmingleevents.com" className="underline" style={{ color: C.purple }}>hello@mixandmingleevents.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default MarketCampPoliciesPage;
